import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import debounce from "debounce";
import { format, parseISO, isValid, getDay } from "date-fns";
import { fr as frLocale, enUS as enLocale } from "date-fns/locale";

import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Collapse from "@mui/material/Collapse";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CheckIcon from "@mui/icons-material/Check";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import Select2Field from "components/Select2Field";
import TeacherAvatar from "components/TeacherAvatar";
import Loading from "components/Loading";
import TablePaginationBar from "components/TablePaginationBar";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import MiniStatisticsCard from "examples/Cards/StatisticsCards/MiniStatisticsCard";

import api from "services/api";
import { apiurl } from "utils";
import { useTranslate } from "utils/i18n";
import { useRegisterAssistantPage } from "assistant/useRegisterAssistantPage";
import { totalPagesFromCount } from "utils/paginationStats";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  figmaTokens,
  directoryTableHeaderCellSx,
  directoryTableHeadStickyOverridesSx,
  directoryTableBodyCellDefaultsSx,
} from "assets/theme/designTokens";

const GEO_API = {
  provinces: `${apiurl}Assignment/provinces`,
  departments: (provid) => `${apiurl}Assignment/departments?provid=${provid}`,
  subprefectures: (dptid) => `${apiurl}Assignment/subprefectures?dptid=${dptid}`,
  establishments: (sptid) => `${apiurl}Assignment/establishments?sptid=${sptid}`,
};

/** Backend permits pageSize values from 1 through 100,000. */
const API_PAGE_SIZE = 100_000;

function attendanceReportErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  return fallback;
}

const CARD_SHELL_SX = {
  overflow: "hidden",
  borderRadius: "16px",
  border: `1px solid ${figmaTokens.borderSubtle}`,
  boxShadow: "0 14px 44px rgba(15, 23, 42, 0.07)",
  bgcolor: figmaTokens.surface,
};

const statWrapSx = {
  minWidth: 0,
  height: "100%",
  display: "flex",
  "& > .MuiCard-root": {
    flex: 1,
    minHeight: 108,
    borderRadius: "16px",
    border: `1px solid ${figmaTokens.borderSubtle}`,
    boxShadow: "0 10px 34px rgba(15, 23, 42, 0.07)",
    backgroundColor: figmaTokens.surface,
    backgroundImage: `linear-gradient(145deg, rgba(102, 126, 234, 0.07) 0%, ${figmaTokens.surface} 52%)`,
    overflow: "hidden",
  },
};

const CALENDAR_STATUS = {
  sunday: { bg: "#f1f5f9", border: "#e2e8f0", color: "#94a3b8" },
  absent: { bg: "#fef2f2", border: "#fecaca", color: "#991b1b" },
  inRadius: { bg: "#d1fae5", border: "#6ee7b7", color: "#065f46" },
  outRadius: { bg: "#ffedd5", border: "#fdba74", color: "#c2410c" },
  present: { bg: "#dbeafe", border: "#93c5fd", color: "#1e40af" },
};

const makeOptions = (data, ids, labels) =>
  (Array.isArray(data) ? data : []).map((item) => ({
    id: ids.map((key) => item[key]).find(Boolean),
    text: labels.map((key) => item[key]).find(Boolean),
  }));

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem("tk446655440000")}` };
}

function toApiDate(value) {
  if (!value) return "";
  return String(value).slice(0, 10);
}

function defaultStartDate() {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return format(d, "yyyy-MM-dd");
}

function defaultEndDate() {
  return format(new Date(), "yyyy-MM-dd");
}

function parseCalendarDate(raw) {
  if (!raw) return null;
  const d = parseISO(String(raw).slice(0, 10));
  return isValid(d) ? d : null;
}

function resolveDayStatus(day) {
  const date = parseCalendarDate(day?.attendDate);
  if (date && getDay(date) === 0) return "sunday";
  if (!day?.isPresent) return "absent";
  if (day?.isWithinRadius) return "inRadius";
  if (day?.isOutsideRadius) return "outRadius";
  return "present";
}

function groupCalendarByMonth(calendar) {
  const groups = new Map();
  (calendar || []).forEach((day) => {
    const date = parseCalendarDate(day?.attendDate);
    if (!date) return;
    const key = format(date, "yyyy-MM");
    if (!groups.has(key)) {
      groups.set(key, { key, date, days: [] });
    }
    groups.get(key).days.push({ ...day, date });
  });
  return Array.from(groups.values()).sort((a, b) => a.key.localeCompare(b.key));
}

function dayLetter(date, language) {
  const dow = getDay(date);
  if (language === "fr") {
    const map = { 0: "D", 1: "L", 2: "M", 3: "M", 4: "J", 5: "V", 6: "S" };
    return map[dow] ?? "";
  }
  const map = { 0: "S", 1: "M", 2: "T", 3: "W", 4: "T", 5: "F", 6: "S" };
  return map[dow] ?? "";
}

function buildTimeline(startDate, endDate) {
  const start = parseCalendarDate(startDate);
  const end = parseCalendarDate(endDate);
  if (!start || !end || start > end) return [];
  const days = [];
  const cur = new Date(start);
  while (cur <= end) {
    days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

function calendarToMap(calendar) {
  const map = new Map();
  (calendar || []).forEach((day) => {
    const d = parseCalendarDate(day?.attendDate);
    if (d) map.set(format(d, "yyyy-MM-dd"), day);
  });
  return map;
}

function extractReportTotal(data, teachers) {
  return (
    Number(
      data?.totalCount ??
        data?.totalTeachers ??
        data?.stats?.totalCount ??
        data?.stats?.totalTeachers ??
        teachers?.[0]?.totalCount ??
        teachers?.[0]?.totalTeachers ??
        0
    ) || teachers.length
  );
}

function dayStatusLabel(day, t) {
  const status = resolveDayStatus(day);
  if (status === "sunday") return t("estab_attendance.legend.sunday", "Sunday (excluded)");
  if (status === "absent") return t("estab_attendance.legend.absent", "Absent");
  if (status === "inRadius") return t("estab_attendance.legend.in_radius", "Present (within radius)");
  if (status === "outRadius") return t("estab_attendance.legend.out_radius", "Present (outside radius)");
  return t("estab_attendance.tooltip.present", "{{date}} — Present").replace("{{date}}", "");
}

function matrixMarkForDay(entry) {
  if (!entry?.isPresent) return "";
  const outRadius = entry.isOutsideRadius === true || entry.isOutsideRadius === 1;
  return outRadius ? "✓ (out)" : "✓";
}

const MATRIX_TICK_IN_COLOR = "#16a34a";
const MATRIX_TICK_OUT_COLOR = "#ea580c";

function matrixTickColor(entry) {
  const outRadius = entry?.isOutsideRadius === true || entry?.isOutsideRadius === 1;
  return outRadius ? MATRIX_TICK_OUT_COLOR : MATRIX_TICK_IN_COLOR;
}

const MATRIX_STICKY_NAME_SX = {
  position: "sticky",
  left: 0,
  zIndex: 2,
  bgcolor: figmaTokens.surface,
  minWidth: 168,
  maxWidth: 220,
  fontWeight: 700,
  fontSize: "0.78rem",
  borderRight: `2px solid ${figmaTokens.borderStrong}`,
  boxShadow: "4px 0 8px rgba(15, 23, 42, 0.04)",
};

const MATRIX_STICKY_HEAD_SX = {
  ...MATRIX_STICKY_NAME_SX,
  zIndex: 4,
  bgcolor: "#f8fafc",
  textDecoration: "underline",
  fontWeight: 800,
  fontSize: "0.72rem",
};

const MATRIX_DAY_HEAD_SX = {
  minWidth: 30,
  width: 30,
  maxWidth: 30,
  px: 0.25,
  py: 0.75,
  textAlign: "center",
  fontWeight: 800,
  fontSize: "0.72rem",
  textDecoration: "underline",
  lineHeight: 1,
  borderRight: `1px solid ${figmaTokens.borderSubtle}`,
};

const MATRIX_DAY_CELL_SX = {
  minWidth: 30,
  width: 30,
  maxWidth: 30,
  px: 0.25,
  py: 0.5,
  textAlign: "center",
  borderRight: `1px solid ${figmaTokens.borderSubtle}`,
  lineHeight: 1,
};

function MatrixLegend({ t }) {
  return (
    <SoftBox display="flex" flexWrap="wrap" gap={2} mb={1.5}>
      <SoftBox display="flex" alignItems="center" gap={0.75}>
        <CheckIcon sx={{ color: MATRIX_TICK_IN_COLOR, fontSize: "1.15rem" }} />
        <SoftTypography variant="caption" sx={{ fontWeight: 600, color: figmaTokens.textSecondary }}>
          {t("estab_attendance.matrix_legend_in", "Present within radius")}
        </SoftTypography>
      </SoftBox>
      <SoftBox display="flex" alignItems="center" gap={0.75}>
        <CheckIcon sx={{ color: MATRIX_TICK_OUT_COLOR, fontSize: "1.15rem" }} />
        <SoftTypography variant="caption" sx={{ fontWeight: 600, color: figmaTokens.textSecondary }}>
          {t("estab_attendance.matrix_legend_out", "Present outside radius")}
        </SoftTypography>
      </SoftBox>
      <SoftBox display="flex" alignItems="center" gap={0.75}>
        <SoftTypography variant="caption" sx={{ fontWeight: 600, color: figmaTokens.textMuted }}>
          {t("estab_attendance.matrix_legend_empty", "Empty = absent or Sunday")}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

MatrixLegend.propTypes = {
  t: PropTypes.func.isRequired,
};

function MatrixDayCell({ day, entry, t }) {
  const dateKey = format(day, "yyyy-MM-dd");
  const isSunday = getDay(day) === 0;

  if (isSunday) {
    return (
      <TableCell
        sx={{
          ...MATRIX_DAY_CELL_SX,
          bgcolor: "#f8fafc",
        }}
      />
    );
  }

  if (!entry?.isPresent) {
    return <TableCell sx={MATRIX_DAY_CELL_SX} />;
  }

  const outRadius = entry.isOutsideRadius === true || entry.isOutsideRadius === 1;
  const inRadius = entry.isWithinRadius === true || entry.isWithinRadius === 1;

  let tooltip = dateKey;
  if (inRadius) {
    tooltip = t("estab_attendance.tooltip.in_radius", "{{date}} — Present within radius").replace(
      "{{date}}",
      dateKey
    );
  } else if (outRadius) {
    tooltip = t("estab_attendance.tooltip.out_radius", "{{date}} — Present outside radius").replace(
      "{{date}}",
      dateKey
    );
  } else {
    tooltip = t("estab_attendance.tooltip.present", "{{date}} — Present").replace("{{date}}", dateKey);
  }

  return (
    <TableCell sx={MATRIX_DAY_CELL_SX}>
      <Tooltip title={tooltip} arrow placement="top">
        <CheckIcon
          sx={{
            fontSize: "1.05rem",
            color: matrixTickColor(entry),
            cursor: "default",
            display: "block",
            mx: "auto",
          }}
        />
      </Tooltip>
    </TableCell>
  );
}

MatrixDayCell.propTypes = {
  day: PropTypes.instanceOf(Date).isRequired,
  entry: PropTypes.object,
  t: PropTypes.func.isRequired,
};

function ConsolidatedAttendanceMatrix({ teachers, startDate, endDate, t, language }) {
  const timeline = useMemo(() => buildTimeline(startDate, endDate), [startDate, endDate]);

  const teacherMaps = useMemo(
    () =>
      teachers.map((teacher) => ({
        teacher,
        map: calendarToMap(teacher.calendar),
      })),
    [teachers]
  );

  if (!timeline.length || !teachers.length) return null;

  return (
    <SoftBox mt={3} pt={3} sx={{ borderTop: `1px solid ${figmaTokens.cardHeaderRule}` }}>
      <SoftTypography variant="h6" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mb: 0.5 }}>
        {t("estab_attendance.matrix_title", "Attendance calendar — all teachers")}
      </SoftTypography>
      <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mb: 1.5, maxWidth: 720 }}>
        {t(
          "estab_attendance.matrix_hint",
          "One row per teacher. Green tick = present within radius; orange tick = present outside radius."
        )}
      </SoftTypography>
      <MatrixLegend t={t} />
      <TableContainer
        component={Paper}
        elevation={0}
        className="estab-attendance-matrix-wrap"
        sx={{
          borderRadius: "12px",
          border: `1px solid ${figmaTokens.borderStrong}`,
          overflow: "auto",
          maxHeight: { xs: "55vh", md: "min(60vh, 520px)" },
        }}
      >
        <Table
          size="small"
          stickyHeader
          className="estab-attendance-matrix"
          sx={{
            borderCollapse: "separate",
            borderSpacing: 0,
            ...directoryTableHeadStickyOverridesSx,
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={MATRIX_STICKY_HEAD_SX}>
                {t("estab_attendance.matrix.col_name", "Nom et prenom")}
              </TableCell>
              {timeline.map((day) => (
                <TableCell key={format(day, "yyyy-MM-dd")} sx={MATRIX_DAY_HEAD_SX}>
                  {dayLetter(day, language)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {teacherMaps.map(({ teacher, map }) => {
              const id = teacher.teacherId ?? teacher.id;
              return (
                <TableRow key={id} hover>
                  <TableCell sx={MATRIX_STICKY_NAME_SX}>
                    <SoftBox display="flex" alignItems="center" gap={1} minWidth={0}>
                      <TeacherAvatar teacher={teacher} size="xs" />
                      <SoftTypography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {teacher.teacherName ?? "—"}
                      </SoftTypography>
                    </SoftBox>
                  </TableCell>
                  {timeline.map((day) => {
                    const key = format(day, "yyyy-MM-dd");
                    return <MatrixDayCell key={key} day={day} entry={map.get(key)} t={t} />;
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </SoftBox>
  );
}

ConsolidatedAttendanceMatrix.propTypes = {
  teachers: PropTypes.arrayOf(PropTypes.object).isRequired,
  startDate: PropTypes.string,
  endDate: PropTypes.string,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
};

function CalendarLegend({ t }) {
  const items = [
    { key: "inRadius", label: t("estab_attendance.legend.in_radius", "Present (within radius)") },
    { key: "outRadius", label: t("estab_attendance.legend.out_radius", "Present (outside radius)") },
    { key: "absent", label: t("estab_attendance.legend.absent", "Absent") },
    { key: "sunday", label: t("estab_attendance.legend.sunday", "Sunday (excluded)") },
  ];
  return (
    <SoftBox display="flex" flexWrap="wrap" gap={1.5} mb={1.5}>
      {items.map((item) => {
        const style = CALENDAR_STATUS[item.key];
        return (
          <SoftBox key={item.key} display="flex" alignItems="center" gap={0.75}>
            <SoftBox
              sx={{
                width: 14,
                height: 14,
                borderRadius: "4px",
                bgcolor: style.bg,
                border: `1px solid ${style.border}`,
              }}
            />
            <SoftTypography variant="caption" sx={{ fontWeight: 600, color: figmaTokens.textSecondary }}>
              {item.label}
            </SoftTypography>
          </SoftBox>
        );
      })}
    </SoftBox>
  );
}

CalendarLegend.propTypes = {
  t: PropTypes.func.isRequired,
};

function AttendanceCalendarGrid({ calendar, t, locale }) {
  const months = useMemo(() => groupCalendarByMonth(calendar), [calendar]);

  if (!months.length) {
    return (
      <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, py: 2 }}>
        {t("estab_attendance.no_calendar", "No attendance days in this period.")}
      </SoftTypography>
    );
  }

  return (
    <SoftBox className="estab-attendance-calendar">
      <CalendarLegend t={t} />
      {months.map((monthGroup) => (
        <SoftBox key={monthGroup.key} mb={2}>
          <SoftTypography
            variant="caption"
            sx={{
              display: "block",
              mb: 1,
              fontWeight: 800,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              color: figmaTokens.textMuted,
            }}
          >
            {format(monthGroup.date, "MMMM yyyy", { locale })}
          </SoftTypography>
          <SoftBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 0.75,
            }}
          >
            {monthGroup.days.map((day) => {
              const status = resolveDayStatus(day);
              const style = CALENDAR_STATUS[status];
              const date = day.date;
              const dayNum = format(date, "d");
              const weekday = format(date, "EEE", { locale });
              const fullDate = format(date, "yyyy-MM-dd");
              let tooltip = fullDate;
              if (status === "inRadius") {
                tooltip = t("estab_attendance.tooltip.in_radius", "{{date}} — Present within radius").replace(
                  "{{date}}",
                  fullDate
                );
              } else if (status === "outRadius") {
                tooltip = t("estab_attendance.tooltip.out_radius", "{{date}} — Present outside radius").replace(
                  "{{date}}",
                  fullDate
                );
              } else if (status === "absent") {
                tooltip = t("estab_attendance.tooltip.absent", "{{date}} — Absent").replace("{{date}}", fullDate);
              } else if (status === "sunday") {
                tooltip = t("estab_attendance.tooltip.sunday", "{{date}} — Sunday").replace("{{date}}", fullDate);
              }

              return (
                <Tooltip key={fullDate} title={tooltip} arrow placement="top">
                  <SoftBox
                    sx={{
                      width: 44,
                      minWidth: 44,
                      height: 52,
                      borderRadius: "10px",
                      border: `1px solid ${style.border}`,
                      bgcolor: style.bg,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "default",
                      transition: "transform 0.15s ease",
                      "&:hover": { transform: "scale(1.06)", zIndex: 1 },
                    }}
                  >
                    <SoftTypography
                      sx={{ fontSize: "0.58rem", fontWeight: 700, color: style.color, lineHeight: 1.1 }}
                    >
                      {weekday}
                    </SoftTypography>
                    <SoftTypography sx={{ fontSize: "0.85rem", fontWeight: 800, color: style.color, lineHeight: 1.2 }}>
                      {dayNum}
                    </SoftTypography>
                  </SoftBox>
                </Tooltip>
              );
            })}
          </SoftBox>
        </SoftBox>
      ))}
    </SoftBox>
  );
}

AttendanceCalendarGrid.propTypes = {
  calendar: PropTypes.arrayOf(PropTypes.object),
  t: PropTypes.func.isRequired,
  locale: PropTypes.object,
};

function TeacherReportRow({ teacher, expanded, onToggle, t, locale }) {
  return (
    <>
      <TableRow
        hover
        sx={{ cursor: "pointer", "& > td": { borderBottom: expanded ? "none" : undefined } }}
        onClick={onToggle}
      >
        <TableCell sx={{ width: 48, px: 1 }}>
          <IconButton size="small" aria-label="expand row">
            {expanded ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <SoftBox display="flex" alignItems="center" gap={1.25} minWidth={0}>
            <TeacherAvatar teacher={teacher} size="sm" />
            <SoftBox minWidth={0}>
              <SoftTypography variant="body2" sx={{ fontWeight: 700 }}>
                {teacher.teacherName ?? "—"}
              </SoftTypography>
              <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted }}>
                {teacher.regNumber ?? "—"}
              </SoftTypography>
            </SoftBox>
          </SoftBox>
        </TableCell>
        <TableCell align="right">{teacher.totalDaysExcludingSundays ?? "—"}</TableCell>
        <TableCell align="right">{teacher.totalDaysPresent ?? "—"}</TableCell>
        <TableCell align="right">{teacher.totalDaysPresentInRadius ?? "—"}</TableCell>
        <TableCell>{teacher.establishmentName ?? "—"}</TableCell>
        <TableCell>{teacher.departmentName ?? "—"}</TableCell>
        <TableCell>{teacher.provinceName ?? "—"}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0, bgcolor: "rgba(247, 249, 252, 0.85)" }}>
          <Collapse in={expanded} timeout="auto" unmountOnExit>
            <SoftBox sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
              <SoftTypography variant="button" sx={{ fontWeight: 800, color: figmaTokens.textPrimary, mb: 1, display: "block" }}>
                {t("estab_attendance.calendar_title", "Attendance calendar")}
              </SoftTypography>
              <AttendanceCalendarGrid calendar={teacher.calendar} t={t} locale={locale} />
            </SoftBox>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

TeacherReportRow.propTypes = {
  teacher: PropTypes.shape({
    teacherId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    teacherName: PropTypes.string,
    regNumber: PropTypes.string,
    totalDaysExcludingSundays: PropTypes.number,
    totalDaysPresent: PropTypes.number,
    totalDaysPresentInRadius: PropTypes.number,
    establishmentName: PropTypes.string,
    departmentName: PropTypes.string,
    provinceName: PropTypes.string,
    calendar: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  locale: PropTypes.object,
};

function EstablishmentAttendanceReport() {
  const { t, language } = useTranslate();
  const locale = language === "fr" ? frLocale : enLocale;

  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");
  const [report, setReport] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const queryReadyRef = useRef(false);

  const [provOpts, setProvOpts] = useState([]);
  const [deptOpts, setDeptOpts] = useState([]);
  const [subpOpts, setSubpOpts] = useState([]);
  const [estabOpts, setEstabOpts] = useState([]);

  const [province, setProvince] = useState("");
  const [department, setDepartment] = useState("");
  const [subprefecture, setSubprefecture] = useState("");
  const [establishment, setEstablishment] = useState("");
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [radius, setRadius] = useState("500");

  const cache = useRef({ provinces: null, departments: {}, subprefectures: {}, establishments: {} });
  const controllers = useRef({});

  const cancelRequest = (key) => {
    if (controllers.current[key]) controllers.current[key].abort();
    controllers.current[key] = new AbortController();
    return controllers.current[key].signal;
  };

  const fetchProvinces = useCallback(async () => {
    if (cache.current.provinces) {
      setProvOpts(cache.current.provinces);
      return;
    }
    try {
      const { data } = await axios.get(GEO_API.provinces, { headers: authHeaders() });
      const opts = makeOptions(data, ["provid", "id"], ["name", "province"]);
      cache.current.provinces = opts;
      setProvOpts(opts);
    } catch (err) {
      console.error("Fetch provinces error", err);
    }
  }, []);

  const fetchDepartments = useCallback(
    debounce(async (provId) => {
      if (!provId) return setDeptOpts([]);
      if (cache.current.departments[provId]) {
        return setDeptOpts(cache.current.departments[provId]);
      }
      try {
        const { data } = await axios.get(GEO_API.departments(provId), {
          signal: cancelRequest("departments"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["dptid", "id"], ["name", "department"]);
        cache.current.departments[provId] = opts;
        setDeptOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch departments error", err);
      }
    }, 300),
    []
  );

  const fetchSubprefectures = useCallback(
    debounce(async (deptId) => {
      if (!deptId) return setSubpOpts([]);
      if (cache.current.subprefectures[deptId]) {
        return setSubpOpts(cache.current.subprefectures[deptId]);
      }
      try {
        const { data } = await axios.get(GEO_API.subprefectures(deptId), {
          signal: cancelRequest("subprefectures"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["sptid", "id"], ["name", "subprefecture"]);
        cache.current.subprefectures[deptId] = opts;
        setSubpOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch subprefectures error", err);
      }
    }, 300),
    []
  );

  const fetchEstablishments = useCallback(
    debounce(async (subpId) => {
      if (!subpId) return setEstabOpts([]);
      if (cache.current.establishments[subpId]) {
        return setEstabOpts(cache.current.establishments[subpId]);
      }
      try {
        const { data } = await axios.get(GEO_API.establishments(subpId), {
          signal: cancelRequest("establishments"),
          headers: authHeaders(),
        });
        const opts = makeOptions(data, ["id"], ["name", "establishment"]);
        cache.current.establishments[subpId] = opts;
        setEstabOpts(opts);
      } catch (err) {
        if (err.name !== "CanceledError") console.error("Fetch establishments error", err);
      }
    }, 300),
    []
  );

  useEffect(() => {
    fetchProvinces();
  }, [fetchProvinces]);

  useEffect(() => {
    fetchDepartments(province);
  }, [province, fetchDepartments]);

  useEffect(() => {
    fetchSubprefectures(department);
  }, [department, fetchSubprefectures]);

  useEffect(() => {
    fetchEstablishments(subprefecture);
  }, [subprefecture, fetchEstablishments]);

  const teachers = useMemo(() => (Array.isArray(report?.teachers) ? report.teachers : []), [report]);

  const totalTeachers = teachers.length;
  const totalPages = totalPagesFromCount(totalTeachers, pageSize);

  const pagedTeachers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return teachers.slice(start, start + pageSize);
  }, [teachers, page, pageSize]);

  const assistantPageSnapshot = useMemo(() => {
    const label = (opts, val) => opts.find((o) => String(o.id) === String(val))?.text;
    return {
      pageKey: "establishment-attendance",
      route: "/rapport-presence-etablissement",
      filters: {
        startDate,
        endDate,
        radius,
        province: label(provOpts, province),
        department: label(deptOpts, department),
        subprefecture: label(subpOpts, subprefecture),
        establishment,
        establishmentLabel: label(estabOpts, establishment),
      },
      counts: { teachers: totalTeachers },
    };
  }, [startDate, endDate, radius, province, department, subprefecture, establishment, provOpts, deptOpts, subpOpts, estabOpts, totalTeachers]);
  useRegisterAssistantPage(assistantPageSnapshot);

  const buildQueryParams = useCallback(() => {
    const params = {
      startDate: toApiDate(startDate),
      endDate: toApiDate(endDate),
      radius: Number(radius),
      pageNumber: 1,
      pageSize: API_PAGE_SIZE,
    };
    if (establishment) params.estabId = establishment;
    return params;
  }, [startDate, endDate, radius, establishment]);

  const validateFilters = useCallback(() => {
    if (!startDate || !endDate) {
      setError(t("estab_attendance.error.dates", "Please select start and end dates."));
      return false;
    }
    if (startDate > endDate) {
      setError(t("estab_attendance.error.date_order", "Start date must be before end date."));
      return false;
    }
    const radiusNum = Number(radius);
    if (!Number.isFinite(radiusNum) || radiusNum <= 0) {
      setError(t("estab_attendance.error.radius", "Please enter a valid radius in meters."));
      return false;
    }
    return true;
  }, [startDate, endDate, radius, t]);

  const fetchReport = useCallback(async () => {
    const { data } = await api.get("/Teacher/establishmentattendancereport", {
      params: buildQueryParams(),
      headers: authHeaders(),
    });
    const list = Array.isArray(data?.teachers) ? data.teachers : [];
    return { data, teachers: list, total: extractReportTotal(data, list) };
  }, [buildQueryParams]);

  const fetchAllTeachers = useCallback(async () => {
    const result = await fetchReport();
    return { ...result.data, teachers: result.teachers, totalCount: result.total || result.teachers.length };
  }, [fetchReport]);

  const runQuery = useCallback(async () => {
    if (!validateFilters()) return;
    setLoading(true);
    setExpandedId(null);
    try {
      const result = await fetchReport();
      setReport(result.data);
      queryReadyRef.current = true;
      if (!result.teachers.length) {
        setError(
          t(
            "estab_attendance.no_teachers",
            "No teachers found for this establishment in the selected period."
          )
        );
      } else {
        setError("");
      }
    } catch (err) {
      console.error("Establishment attendance report error", err);
      setReport(null);
      queryReadyRef.current = false;
      setError(
        attendanceReportErrorMessage(
          err,
          t("estab_attendance.error.load", "Failed to load the attendance report.")
        )
      );
    } finally {
      setLoading(false);
    }
  }, [fetchReport, t, validateFilters]);

  useEffect(() => {
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load report on mount with default filters
  }, []);

  const reportStats = useMemo(() => {
    if (!teachers.length) {
      return { present: 0, inRadius: 0, avgRate: 0 };
    }
    const present = teachers.reduce((s, row) => s + Number(row.totalDaysPresent ?? 0), 0);
    const inRadius = teachers.reduce((s, row) => s + Number(row.totalDaysPresentInRadius ?? 0), 0);
    const totalDays = Number(report?.totalDaysExcludingSundays ?? teachers[0]?.totalDaysExcludingSundays ?? 0);
    const teacherCount = teachers.length;
    const avgRate =
      totalDays > 0 && teacherCount > 0
        ? Math.round((present / (totalDays * teacherCount)) * 100)
        : 0;
    return { present, inRadius, avgRate };
  }, [teachers, report]);

  const loadReport = async (event) => {
    event?.preventDefault?.();
    setError("");
    setPage(1);
    await runQuery();
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  const exportExcel = async () => {
    if (!validateFilters()) return;
    setExporting(true);
    try {
      const full = await fetchAllTeachers();
      const allTeachers = full.teachers || [];
      const wb = XLSX.utils.book_new();

      const summaryRows = allTeachers.map((row) => ({
        [t("estab_attendance.col.teacher", "Teacher")]: row.teacherName ?? "",
        [t("home.table.reg_number", "Teacher reg #")]: row.regNumber ?? "",
        [t("estab_attendance.col.workdays", "Workdays")]: row.totalDaysExcludingSundays ?? "",
        [t("estab_attendance.col.present", "Present")]: row.totalDaysPresent ?? "",
        [t("estab_attendance.col.in_radius", "In radius")]: row.totalDaysPresentInRadius ?? "",
        [t("estab_attendance.col.establishment", "Establishment")]: row.establishmentName ?? "",
        [t("estab_attendance.col.department", "Department")]: row.departmentName ?? "",
        [t("estab_attendance.col.province", "Province")]: row.provinceName ?? "",
      }));
      XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(summaryRows), t("estab_attendance.export.summary", "Summary"));

      const detailRows = [];
      allTeachers.forEach((teacher) => {
        (teacher.calendar || []).forEach((day) => {
          detailRows.push({
            [t("estab_attendance.col.teacher", "Teacher")]: teacher.teacherName ?? "",
            [t("home.table.reg_number", "Teacher reg #")]: teacher.regNumber ?? "",
            [t("estab_attendance.export.date", "Date")]: toApiDate(day.attendDate),
            [t("estab_attendance.export.status", "Status")]: dayStatusLabel(day, t),
            [t("estab_attendance.col.establishment", "Establishment")]: teacher.establishmentName ?? "",
          });
        });
      });
      if (detailRows.length) {
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.json_to_sheet(detailRows),
          t("estab_attendance.export.daily", "Daily")
        );
      }

      const timeline = buildTimeline(full.startDate ?? startDate, full.endDate ?? endDate);
      if (timeline.length && allTeachers.length) {
        const matrixHeader = [
          t("estab_attendance.matrix.col_name", "Name"),
          ...timeline.map((day) => format(day, "yyyy-MM-dd")),
        ];
        const matrixRows = allTeachers.map((teacher) => {
          const map = calendarToMap(teacher.calendar);
          return [
            teacher.teacherName ?? "",
            ...timeline.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              if (getDay(day) === 0) return "";
              return matrixMarkForDay(map.get(key));
            }),
          ];
        });
        XLSX.utils.book_append_sheet(
          wb,
          XLSX.utils.aoa_to_sheet([matrixHeader, ...matrixRows]),
          t("estab_attendance.export.matrix", "Matrix")
        );
      }

      const buffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        `establishment-attendance-${toApiDate(startDate)}-${toApiDate(endDate)}.xlsx`
      );
    } catch (err) {
      console.error("Excel export failed", err);
      setError(t("estab_attendance.export.error", "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const exportPDF = async () => {
    if (!validateFilters()) return;
    setExporting(true);
    try {
      const full = await fetchAllTeachers();
      const allTeachers = full.teachers || [];
      const doc = new jsPDF("l", "pt", "a4");
      const margin = 28;
      const pageW = doc.internal.pageSize.getWidth();

      doc.setFontSize(14);
      doc.setTextColor(33, 113, 255);
      doc.text(t("estab_attendance.title", "Establishment attendance report"), margin, 32);
      doc.setFontSize(9);
      doc.setTextColor(80);
      doc.text(
        `${toApiDate(full.startDate ?? startDate)} → ${toApiDate(full.endDate ?? endDate)} · ${t(
          "estab_attendance.radius",
          "Radius (meters)"
        )}: ${full.radius ?? radius} · ${allTeachers.length} ${t("estab_attendance.stats.teachers", "Teachers")}`,
        margin,
        48
      );

      autoTable(doc, {
        startY: 58,
        head: [
          [
            t("estab_attendance.col.teacher", "Teacher"),
            t("home.table.reg_number", "Teacher reg #"),
            t("estab_attendance.col.workdays", "Workdays"),
            t("estab_attendance.col.present", "Present"),
            t("estab_attendance.col.in_radius", "In radius"),
            t("estab_attendance.col.establishment", "Establishment"),
            t("estab_attendance.col.department", "Department"),
            t("estab_attendance.col.province", "Province"),
          ],
        ],
        body: allTeachers.map((row) => [
          row.teacherName ?? "",
          row.regNumber ?? "",
          row.totalDaysExcludingSundays ?? "",
          row.totalDaysPresent ?? "",
          row.totalDaysPresentInRadius ?? "",
          row.establishmentName ?? "",
          row.departmentName ?? "",
          row.provinceName ?? "",
        ]),
        styles: { fontSize: 8, cellPadding: 4 },
        headStyles: { fillColor: [30, 41, 59] },
        margin: { left: margin, right: margin },
      });

      const detailRows = [];
      allTeachers.forEach((teacher) => {
        (teacher.calendar || []).forEach((day) => {
          detailRows.push([
            teacher.teacherName ?? "",
            teacher.regNumber ?? "",
            toApiDate(day.attendDate),
            dayStatusLabel(day, t),
            teacher.establishmentName ?? "",
          ]);
        });
      });

      if (detailRows.length) {
        doc.addPage();
        doc.setFontSize(12);
        doc.setTextColor(33, 113, 255);
        doc.text(t("estab_attendance.export.daily", "Daily attendance"), margin, 32);
        autoTable(doc, {
          startY: 44,
          head: [
            [
              t("estab_attendance.col.teacher", "Teacher"),
              t("home.table.reg_number", "Teacher reg #"),
              t("estab_attendance.export.date", "Date"),
              t("estab_attendance.export.status", "Status"),
              t("estab_attendance.col.establishment", "Establishment"),
            ],
          ],
          body: detailRows,
          styles: { fontSize: 7, cellPadding: 3 },
          headStyles: { fillColor: [30, 41, 59] },
          margin: { left: margin, right: margin },
        });
      }

      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i += 1) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(120);
        doc.text(`${i} / ${totalPages}`, pageW - margin, doc.internal.pageSize.getHeight() - 16, { align: "right" });
      }

      doc.save(`establishment-attendance-${toApiDate(startDate)}-${toApiDate(endDate)}.pdf`);
    } catch (err) {
      console.error("PDF export failed", err);
      setError(t("estab_attendance.export.error", "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const firstTeacher = teachers[0];
  const periodLabel =
    report?.startDate && report?.endDate
      ? `${toApiDate(report.startDate)} → ${toApiDate(report.endDate)}`
      : null;

  return (
    <DashboardLayout>
      {loading && <Loading />}
      {exporting && <Loading />}
      <DashboardNavbar />
      <SoftBox py={3} className="content-area-main" sx={{ bgcolor: figmaTokens.pageBg }}>
        <Card sx={{ ...CARD_SHELL_SX, mb: 3 }}>
          <SoftBox sx={{ height: 5, background: figmaTokens.gradientCSS }} />
          <SoftBox sx={{ p: { xs: 2, md: 3 } }}>
            <SoftTypography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: "0.14em", color: figmaTokens.textMuted, fontSize: "0.68rem" }}
            >
              {t("estab_attendance.page_kicker", "ATTENDANCE REPORT")}
            </SoftTypography>
            <SoftTypography variant="h4" fontWeight="bold" sx={{ color: figmaTokens.textPrimary, mt: 0.5 }}>
              {t("estab_attendance.title", "Establishment attendance report")}
            </SoftTypography>
            <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mt: 1, maxWidth: 640 }}>
              {t(
                "estab_attendance.page_hint",
                "Select dates and radius to review teacher attendance. Optionally filter by establishment — leave empty to load all teachers."
              )}
            </SoftTypography>
          </SoftBox>
        </Card>

        <Card sx={{ ...CARD_SHELL_SX, mb: 3 }}>
          <SoftBox
            component="form"
            onSubmit={loadReport}
            sx={{ p: { xs: 2, md: 3 }, borderBottom: report ? `1px solid ${figmaTokens.cardHeaderRule}` : "none" }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.province", "Province")}
                </SoftTypography>
                <Select2Field
                  name="province"
                  options={[{ id: "", text: t("estab_attendance.select_province", "Select province") }, ...provOpts]}
                  value={province}
                  onChange={(val) => {
                    setProvince(val);
                    setDepartment("");
                    setSubprefecture("");
                    setEstablishment("");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.department", "Department")}
                </SoftTypography>
                <Select2Field
                  name="department"
                  options={[{ id: "", text: t("estab_attendance.select_department", "Select department") }, ...deptOpts]}
                  value={department}
                  disabled={!province}
                  onChange={(val) => {
                    setDepartment(val);
                    setSubprefecture("");
                    setEstablishment("");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.subprefecture", "Sub-prefecture")}
                </SoftTypography>
                <Select2Field
                  name="subprefecture"
                  options={[
                    { id: "", text: t("estab_attendance.select_subprefecture", "Select sub-prefecture") },
                    ...subpOpts,
                  ]}
                  value={subprefecture}
                  disabled={!department}
                  onChange={(val) => {
                    setSubprefecture(val);
                    setEstablishment("");
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.establishment", "Establishment")}
                </SoftTypography>
                <Select2Field
                  name="establishment"
                  options={[
                    { id: "", text: t("estab_attendance.all_establishments", "All establishments (optional)") },
                    ...estabOpts,
                  ]}
                  value={establishment}
                  onChange={setEstablishment}
                />
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                  {t(
                    "estab_attendance.establishment_optional_hint",
                    "Leave empty to include all teachers matching the date range and radius."
                  )}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.start_date", "Start date")}
                </SoftTypography>
                <SoftInput type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.end_date", "End date")}
                </SoftTypography>
                <SoftInput type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </Grid>
              <Grid item xs={12} md={3}>
                <SoftTypography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: "block" }}>
                  {t("estab_attendance.radius", "Radius (meters)")}
                </SoftTypography>
                <SoftInput
                  type="number"
                  min={1}
                  value={radius}
                  onChange={(e) => setRadius(e.target.value)}
                  placeholder="500"
                />
                <SoftTypography variant="caption" sx={{ color: figmaTokens.textMuted, mt: 0.5, display: "block" }}>
                  {t("estab_attendance.radius_hint", "Maximum distance from establishment to count as in-radius.")}
                </SoftTypography>
              </Grid>
              <Grid item xs={12} md={3} display="flex" alignItems="flex-end" flexWrap="wrap" gap={1}>
                <SoftButton type="submit" variant="gradient" color="info" sx={{ fontWeight: 700, flex: 1, minWidth: 140 }}>
                  <Icon sx={{ mr: 0.75, fontSize: "1.1rem !important" }}>search</Icon>
                  {t("estab_attendance.search", "Generate report")}
                </SoftButton>
              </Grid>
              <Grid item xs={12} display="flex" flexWrap="wrap" gap={1} justifyContent={{ xs: "stretch", md: "flex-end" }}>
                <SoftButton variant="outlined" color="secondary" onClick={exportPDF} disabled={exporting}>
                  <Icon sx={{ mr: 0.5, fontSize: "1rem !important" }}>picture_as_pdf</Icon>
                  {t("estab_attendance.export_pdf", "Export PDF")}
                </SoftButton>
                <SoftButton variant="outlined" color="secondary" onClick={exportExcel} disabled={exporting}>
                  <Icon sx={{ mr: 0.5, fontSize: "1rem !important" }}>table_view</Icon>
                  {t("estab_attendance.export_excel", "Export Excel")}
                </SoftButton>
              </Grid>
            </Grid>
            {error && (
              <Alert severity={teachers.length ? "info" : "warning"} sx={{ mt: 2, borderRadius: "12px" }}>
                {error}
              </Alert>
            )}
          </SoftBox>

          {report && teachers.length > 0 && (
            <SoftBox sx={{ p: { xs: 2, md: 3 } }}>
              <SoftBox
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" },
                  mb: 2.5,
                }}
              >
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("estab_attendance.stats.workdays", "Workdays (excl. Sun)") }}
                    count={report.totalDaysExcludingSundays ?? firstTeacher?.totalDaysExcludingSundays ?? 0}
                    percentage={{ color: "success", text: periodLabel ?? "" }}
                    icon={{ color: "info", component: "calendar_month" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("estab_attendance.stats.teachers", "Teachers") }}
                    count={totalTeachers || teachers.length}
                    percentage={{
                      color: "success",
                      text: establishment
                        ? firstTeacher?.establishmentName ?? ""
                        : t("estab_attendance.all_establishments_short", "All establishments"),
                    }}
                    icon={{ color: "primary", component: "groups" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("estab_attendance.stats.present_total", "Total present days") }}
                    count={reportStats.present}
                    percentage={{ color: "success", text: `${reportStats.avgRate}% avg` }}
                    icon={{ color: "success", component: "check_circle" }}
                  />
                </SoftBox>
                <SoftBox sx={statWrapSx}>
                  <MiniStatisticsCard
                    title={{ text: t("estab_attendance.stats.in_radius_total", "In-radius days") }}
                    count={reportStats.inRadius}
                    percentage={{
                      color: "success",
                      text: t("estab_attendance.stats.radius_label", "{{m}} m radius").replace(
                        "{{m}}",
                        String(report.radius ?? radius)
                      ),
                    }}
                    icon={{ color: "warning", component: "my_location" }}
                  />
                </SoftBox>
              </SoftBox>

              {(firstTeacher?.provinceName || firstTeacher?.departmentName) && (
                <SoftTypography variant="body2" sx={{ color: figmaTokens.textSecondary, mb: 2 }}>
                  {[firstTeacher?.establishmentName, firstTeacher?.departmentName, firstTeacher?.provinceName]
                    .filter(Boolean)
                    .join(" · ")}
                </SoftTypography>
              )}

              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  borderRadius: "14px",
                  border: `1px solid ${figmaTokens.borderSubtle}`,
                  overflow: "auto",
                  maxHeight: { xs: "70vh", md: "min(75vh, 800px)" },
                }}
              >
                <Table
                  size="small"
                  stickyHeader
                  sx={{
                    minWidth: 960,
                    ...directoryTableHeadStickyOverridesSx,
                    ...directoryTableBodyCellDefaultsSx,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ ...directoryTableHeaderCellSx, width: 48 }} />
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.teacher", "Teacher")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.workdays", "Workdays")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.present", "Present")}
                      </TableCell>
                      <TableCell align="right" sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.in_radius", "In radius")}
                      </TableCell>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.establishment", "Establishment")}
                      </TableCell>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.department", "Department")}
                      </TableCell>
                      <TableCell sx={directoryTableHeaderCellSx}>
                        {t("estab_attendance.col.province", "Province")}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedTeachers.map((teacher) => {
                      const id = teacher.teacherId ?? teacher.id;
                      const expanded = expandedId === id;
                      return (
                        <TeacherReportRow
                          key={id}
                          teacher={teacher}
                          expanded={expanded}
                          onToggle={() => setExpandedId(expanded ? null : id)}
                          t={t}
                          locale={locale}
                        />
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePaginationBar
                page={page}
                totalCount={totalTeachers}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeLabel={t("pagination.page_size", "Rows per page")}
              />
              <SoftTypography variant="caption" sx={{ display: "block", mt: 1.5, color: figmaTokens.textMuted }}>
                {t(
                  "estab_attendance.expand_hint",
                  "Click a teacher row to expand the day-by-day attendance calendar."
                )}
              </SoftTypography>

              <ConsolidatedAttendanceMatrix
                teachers={teachers}
                startDate={report?.startDate ?? startDate}
                endDate={report?.endDate ?? endDate}
                t={t}
                language={language}
              />
            </SoftBox>
          )}
        </Card>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default EstablishmentAttendanceReport;
